import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "20";
const height = "20";
const viewBox = "0 0 20 20";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    small: { width: 17, height: 17 },
    medium: { width: 24, height: 24 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            d="M8.113 9.379C9.192 8.857 9.8 8.007 9.8 6.79c0-2.311-1.599-3.153-3.962-3.153H0v12.484h6.012c2.24 0 4.379-1.079 4.379-3.598 0-1.545-.764-2.709-2.278-3.144zM2.494 5.73h2.789c.972 0 1.876.244 1.876 1.426 0 1.042-.712 1.475-1.668 1.475H2.494V5.73zm3.152 8.374H2.494v-3.406h3.205c1.165 0 1.885.505 1.885 1.773 0 1.287-.859 1.633-1.937 1.633zm9.979-7.21c-2.727 0-4.613 2.05-4.613 4.743 0 2.779 1.781 4.726 4.613 4.726 2.034 0 3.615-1.112 4.309-3.232h-2.171c-.156.556-1.059 1.373-2.052 1.373-1.371 0-2.136-1-2.205-2.564h6.48c.173-2.762-1.304-5.047-4.361-5.047zm-2.119 3.431c.034-.694.486-1.571 2.049-1.571 1.198 0 1.738.322 1.965 1.571h-4.014zM18.3 4.476h-4.971v1.668H18.3V4.476z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Behance"
});
